import React, { useState } from 'react';
import {
  Alert, Box, Card, CardContent, Chip, Divider, IconButton,
  Link, Stack, Table, TableBody, TableCell, TableHead,
  TableRow, Tooltip, Typography,
} from '@mui/material';
import {
  CheckCircle, ContentCopy, DnsOutlined, InfoOutlined, WarningAmberOutlined,
} from '@mui/icons-material';

/**
 * DNS Setup Page — Admin Portal → Platform → DNS Setup
 *
 * Shows DNS configuration status based on env vars injected at build time by
 * buildspec-deploy.yml from CloudFormation outputs.
 *
 * If Route 53 hosted zone was found and records were created automatically,
 * shows a success state with the configured records.
 *
 * If not found, shows manual setup instructions with the exact values to use
 * for any DNS provider (Route 53, Cloudflare, GoDaddy, Namecheap, etc.)
 */

const DNS_STATUS         = process.env.REACT_APP_DNS_STATUS         || 'manual-required';
const DOMAIN_NAME        = process.env.REACT_APP_DOMAIN_NAME        || 'example.com';
const ADMIN_SUBDOMAIN    = process.env.REACT_APP_ADMIN_SUBDOMAIN    || 'admin';
const TENANT_SUBDOMAIN   = process.env.REACT_APP_TENANT_SUBDOMAIN   || 'app';
const API_SUBDOMAIN      = process.env.REACT_APP_API_SUBDOMAIN      || 'api';
const ADMIN_DNS_TARGET   = process.env.REACT_APP_ADMIN_DNS_TARGET   || '';
const TENANT_DNS_TARGET  = process.env.REACT_APP_TENANT_DNS_TARGET  || '';
const API_DNS_TARGET     = process.env.REACT_APP_API_DNS_TARGET     || '';

const isAutoConfigured = DNS_STATUS === 'auto-configured';

const DNS_RECORDS = [
  {
    name:    `${ADMIN_SUBDOMAIN}.${DOMAIN_NAME}`,
    type:    'CNAME / Alias',
    target:  ADMIN_DNS_TARGET,
    purpose: 'Admin Portal',
    note:    'Points to CloudFront. Use ALIAS in Route 53, CNAME everywhere else.',
  },
  {
    name:    `${TENANT_SUBDOMAIN}.${DOMAIN_NAME}`,
    type:    'CNAME / Alias',
    target:  TENANT_DNS_TARGET,
    purpose: 'Tenant Portal',
    note:    'Points to CloudFront. Use ALIAS in Route 53, CNAME everywhere else.',
  },
  {
    name:    `${API_SUBDOMAIN}.${DOMAIN_NAME}`,
    type:    'CNAME / Alias',
    target:  API_DNS_TARGET,
    purpose: 'REST API',
    note:    'Points to API Gateway regional endpoint. EvaluateTargetHealth=true recommended.',
  },
];

function CopyButton({ value }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <Tooltip title={copied ? 'Copied!' : 'Copy'}>
      <IconButton size="small" onClick={handleCopy} sx={{ ml: 0.5, p: 0.5 }}>
        <ContentCopy sx={{ fontSize: 14, color: copied ? '#22c55e' : 'text.secondary' }} />
      </IconButton>
    </Tooltip>
  );
}

function MonospaceCell({ value }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Typography
        variant="body2"
        sx={{ fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all' }}
      >
        {value || <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>not available</span>}
      </Typography>
      {value && <CopyButton value={value} />}
    </Box>
  );
}

export default function DnsSetupPage() {
  return (
    <Box maxWidth={900} mx="auto" px={2} py={4}>
      <Stack direction="row" spacing={1.5} alignItems="center" mb={0.5}>
        <DnsOutlined sx={{ color: 'primary.main', fontSize: 28 }} />
        <Typography variant="h5" fontWeight={700}>DNS Configuration</Typography>
        <Chip
          label={isAutoConfigured ? 'Auto-configured' : 'Manual setup required'}
          color={isAutoConfigured ? 'success' : 'warning'}
          size="small"
          icon={isAutoConfigured ? <CheckCircle /> : <WarningAmberOutlined />}
          sx={{ ml: 1 }}
        />
      </Stack>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Domain: <strong>{DOMAIN_NAME}</strong>
      </Typography>

      {/* ── Auto-configured state ── */}
      {isAutoConfigured && (
        <Alert
          severity="success"
          icon={<CheckCircle />}
          sx={{ mb: 3, borderRadius: 2 }}
        >
          <Typography variant="body2" fontWeight={600}>
            Route 53 hosted zone found — DNS records created automatically.
          </Typography>
          <Typography variant="body2">
            All alias records (A + AAAA) were created in Route 53 during deployment.
            No manual action required. DNS propagation typically completes within 60 seconds.
          </Typography>
        </Alert>
      )}

      {/* ── Manual-required state ── */}
      {!isAutoConfigured && (
        <Alert
          severity="warning"
          icon={<WarningAmberOutlined />}
          sx={{ mb: 3, borderRadius: 2 }}
        >
          <Typography variant="body2" fontWeight={600} mb={0.5}>
            No Route 53 hosted zone found for <strong>{DOMAIN_NAME}</strong>.
          </Typography>
          <Typography variant="body2">
            DNS records were not created automatically. Add the records below to your
            DNS provider to make the platform accessible. This is a one-time step.
          </Typography>
        </Alert>
      )}

      {/* ── DNS Records Table ── */}
      <Card variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
        <CardContent sx={{ p: 0 }}>
          <Box px={3} pt={2.5} pb={1.5}>
            <Typography variant="subtitle1" fontWeight={700}>
              {isAutoConfigured ? 'Configured records' : 'Records to create'}
            </Typography>
            {!isAutoConfigured && (
              <Typography variant="body2" color="text.secondary">
                Add these three records to complete your DNS setup.
              </Typography>
            )}
          </Box>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f9fafb' }}>
                <TableCell sx={{ fontWeight: 700, width: '25%' }}>Name (hostname)</TableCell>
                <TableCell sx={{ fontWeight: 700, width: '12%' }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Target (value)</TableCell>
                <TableCell sx={{ fontWeight: 700, width: '18%' }}>Purpose</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {DNS_RECORDS.map(rec => (
                <TableRow key={rec.name} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                  <TableCell>
                    <MonospaceCell value={rec.name} />
                    {isAutoConfigured && (
                      <CheckCircle sx={{ fontSize: 12, color: '#22c55e', ml: 0.5, verticalAlign: 'middle' }} />
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip label={rec.type} size="small" variant="outlined" sx={{ fontSize: 11 }} />
                  </TableCell>
                  <TableCell>
                    <MonospaceCell value={rec.target} />
                    <Typography variant="caption" color="text.secondary" display="block" mt={0.25}>
                      {rec.note}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>{rec.purpose}</Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* ── Provider-specific instructions (only when manual) ── */}
      {!isAutoConfigured && (
        <>
          <Typography variant="subtitle1" fontWeight={700} mb={2}>
            Provider-specific instructions
          </Typography>
          <Stack spacing={2}>
            <ProviderCard
              title="Route 53 (same AWS account, different hosted zone)"
              steps={[
                `Open Route 53 → Hosted Zones → ${DOMAIN_NAME}`,
                'Click "Create record" for each entry above',
                'Set Record type = A, toggle "Alias" ON',
                'For admin and app records: choose "Alias to CloudFront distribution" and paste the target',
                'For the api record: choose "Alias to API Gateway APIs", select your region, and choose the endpoint',
                'Leave TTL at default — alias records don\'t use TTL',
                'Save each record',
              ]}
            />
            <ProviderCard
              title="Cloudflare"
              steps={[
                `Log in → ${DOMAIN_NAME} → DNS → Records`,
                'Add Record → Type = CNAME',
                'Name: enter only the subdomain part (e.g. admin, app, api)',
                'Target: paste the target value from the table above',
                'Proxy status: set to "DNS only" (grey cloud) — do NOT proxy through Cloudflare for CloudFront or API Gateway',
                'TTL: Auto',
                'Repeat for all three records',
              ]}
            />
            <ProviderCard
              title="GoDaddy / Namecheap / Other"
              steps={[
                `Log in to your domain registrar's DNS management panel for ${DOMAIN_NAME}`,
                'Add a new CNAME record for each entry above',
                'Host / Name: enter the subdomain only (admin, app, api) — the registrar adds the root domain automatically',
                'Value / Points to: paste the target from the table',
                'TTL: 300 seconds (5 minutes) or Auto',
                'Save and allow up to 30 minutes for propagation',
              ]}
            />
          </Stack>

          <Alert severity="info" icon={<InfoOutlined />} sx={{ mt: 3, borderRadius: 2 }}>
            <Typography variant="body2" fontWeight={600} mb={0.5}>
              Moving your domain to Route 53?
            </Typography>
            <Typography variant="body2">
              If you add {DOMAIN_NAME} as a hosted zone in Route 53 in the same AWS account,
              re-deploying the platform will auto-detect it and create the records automatically —
              you won&apos;t need to manage them manually anymore.{' '}
              <Link
                href="https://docs.aws.amazon.com/Route53/latest/DeveloperGuide/MigratingDNS.html"
                target="_blank"
                rel="noopener"
              >
                Migration guide →
              </Link>
            </Typography>
          </Alert>
        </>
      )}

      {/* ── Verification section ── */}
      <Card variant="outlined" sx={{ borderRadius: 2, mt: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="subtitle1" fontWeight={700} mb={1.5}>
            Verify DNS propagation
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Run these commands from any terminal after adding the records:
          </Typography>
          <Stack spacing={1.5}>
            {DNS_RECORDS.map(rec => (
              <Box
                key={rec.name}
                sx={{ bgcolor: '#1e293b', borderRadius: 1.5, px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontFamily: 'monospace', color: '#e2e8f0', fontSize: 13 }}
                >
                  {`dig +short ${rec.name}`}
                </Typography>
                <CopyButton value={`dig +short ${rec.name}`} />
              </Box>
            ))}
          </Stack>
          <Typography variant="caption" color="text.secondary" display="block" mt={1.5}>
            Each command should return the target CloudFront or API Gateway domain once propagation is complete.
            Use{' '}
            <Link href="https://dnschecker.org" target="_blank" rel="noopener">dnschecker.org</Link>
            {' '}to check propagation across global resolvers.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}

function ProviderCard({ title, steps }) {
  const [open, setOpen] = useState(false);
  return (
    <Card variant="outlined" sx={{ borderRadius: 2 }}>
      <CardContent
        sx={{ p: 2, cursor: 'pointer', '&:last-child': { pb: 2 } }}
        onClick={() => setOpen(v => !v)}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="body2" fontWeight={600}>{title}</Typography>
          <Typography variant="caption" color="primary.main">
            {open ? 'Hide ▲' : 'Show steps ▼'}
          </Typography>
        </Stack>
        {open && (
          <Box mt={1.5}>
            <Divider sx={{ mb: 1.5 }} />
            <ol style={{ margin: 0, paddingLeft: 20 }}>
              {steps.map((step, i) => (
                <li key={i}>
                  <Typography variant="body2" sx={{ mb: 0.75 }}>{step}</Typography>
                </li>
              ))}
            </ol>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
